import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import {
  MoneyCell,
  SkuCell,
  TransactionLineTable,
  type LineColumn,
} from '../components/TransactionLineTable.tsx';
import { apiFetch } from '../lib/api.ts';
import { dateOnly, money, num, statusClass, statusLabel } from '../lib/format.ts';

interface Line {
  lineNo: number;
  productCode: string | null;
  description: string | null;
  qty: number;
  unitPrice: number;
  subTotal: number;
}

interface Detail {
  id: number;
  purchaseNo: string;
  purchaseDate: string;
  sourcePoNo: string | null;
  creditorNo: string | null;
  creditorName: string | null;
  currency: string;
  value: number;
  status: string;
  lines: Line[];
}

export function PurchaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['purchase', id],
    queryFn: () =>
      apiFetch<Detail>(`/api/dashboard/purchases/${encodeURIComponent(id ?? '')}`),
    enabled: !!id,
  });

  const productLines = (data?.lines ?? []).filter((l) => l.productCode);
  const columns: LineColumn<Line>[] = [
    { label: '#', className: 'mono', render: (l) => l.lineNo },
    { label: 'SKU', className: 'mono', render: (l) => <SkuCell code={l.productCode} /> },
    { label: 'Description', render: (l) => l.description || '—' },
    { label: 'Qty', className: 'num', render: (l) => num(l.qty) },
    { label: 'Unit price', className: 'num', render: (l) => <MoneyCell amount={l.unitPrice} currency={data?.currency ?? 'MYR'} /> },
    { label: 'Subtotal', className: 'num', render: (l) => <MoneyCell amount={l.subTotal} currency={data?.currency ?? 'MYR'} /> },
  ];

  return (
    <>
      <Link to="/purchases" className="back-link">← All purchases</Link>
      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}
      {data && (
        <>
          <header className="main-header">
            <div>
              <h1 className="main-title">Purchase {data.purchaseNo}</h1>
              <span className="main-subtitle mono">{dateOnly(data.purchaseDate)}</span>
            </div>
            <span className={`badge ${statusClass(data.status)}`}>{statusLabel(data.status)}</span>
          </header>
          <section className="panel">
            <header className="panel-header"><h2 className="panel-title">Header</h2></header>
            <dl className="detail-grid">
              <dt>Purchase no</dt><dd className="mono">{data.purchaseNo}</dd>
              <dt>Source PO</dt>
              <dd className="mono">
                {data.sourcePoNo ? (
                  <Link to={`/purchase-orders/${encodeURIComponent(data.sourcePoNo)}`}>
                    {data.sourcePoNo}
                  </Link>
                ) : '—'}
              </dd>
              <dt>Date</dt><dd>{dateOnly(data.purchaseDate)}</dd>
              <dt>Supplier</dt>
              <dd>
                {data.creditorNo ? (
                  <Link to={`/suppliers/${encodeURIComponent(data.creditorNo)}`}>
                    {data.creditorName ?? data.creditorNo}
                  </Link>
                ) : '—'}
              </dd>
              <dt>Value</dt><dd>{money(data.value, data.currency)}</dd>
            </dl>
          </section>
          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Line items</h2>
              <span className="panel-subtitle">{productLines.length} item{productLines.length === 1 ? '' : 's'}</span>
            </header>
            <TransactionLineTable lines={productLines} columns={columns} />
          </section>
        </>
      )}
    </>
  );
}
